import { useCallback, useEffect, useRef, useState } from 'react'
import type { TranscriptUtterance } from '@/client/types.gen'
import type { TranscriptSaveStatus } from '@/components/project/artifact-viewer/transcript/TranscriptSaveStatusIndicator'
import { reconstructPlainText } from '@/components/project/artifact-viewer/transcript/utils'

const SAVE_LOCK_DELAY_MS = 100

/**
 * Compare two Maps for equality
 */
function mapsEqual(a: Map<number, string>, b: Map<number, string>): boolean {
	if (a === b) return true
	if (a.size !== b.size) return false
	for (const [key, value] of a) {
		if (b.get(key) !== value) return false
	}
	return true
}

interface UseTranscriptEditorProps {
	/** Unique identifier for the artifact being edited - used to reset state when switching files */
	artifactId: string
	utterances: TranscriptUtterance[] | null
	onSave: (content: string) => Promise<boolean>
}

interface UseTranscriptEditorReturn {
	editedUtterances: Map<number, string>
	editedSpeakers: Map<number, string>
	saveStatus: TranscriptSaveStatus
	updateUtterance: (index: number, text: string, newSpeaker?: string) => void
	hasUnsavedChanges: boolean
	save: () => Promise<void>
}

interface EditsState {
	texts: Map<number, string>
	speakers: Map<number, string>
}

function editsStateEqual(a: EditsState, b: EditsState): boolean {
	return mapsEqual(a.texts, b.texts) && mapsEqual(a.speakers, b.speakers)
}

function hasPendingEdits(edits: EditsState): boolean {
	return edits.texts.size > 0 || edits.speakers.size > 0
}

/**
 * Transcript editor with manual save only (no debounced autosave).
 *
 * Each save calls the transcript API, which can schedule chunk re-embedding (Bedrock).
 * Intentional saves limit how often that runs; pair with backend unchanged-chunk
 * skip on Atlas ingestion (PR6) for duplicate/no-op saves.
 */
export function useTranscriptEditor({
	artifactId,
	utterances,
	onSave,
}: UseTranscriptEditorProps): UseTranscriptEditorReturn {
	const [editedUtterances, setEditedUtterances] = useState<Map<number, string>>(new Map())
	const [editedSpeakers, setEditedSpeakers] = useState<Map<number, string>>(new Map())
	const [saveStatus, setSaveStatus] = useState<TranscriptSaveStatus>('saved')
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

	const hasInitializedRef = useRef(false)
	const isSavingRef = useRef(false)
	const lastSavedEditsRef = useRef<EditsState>({ texts: new Map(), speakers: new Map() })
	const hasUnsavedChangesRef = useRef(false)
	const lastArtifactIdRef = useRef<string>(artifactId)
	const editedUtterancesRef = useRef(editedUtterances)
	const editedSpeakersRef = useRef(editedSpeakers)

	lastArtifactIdRef.current = artifactId
	editedUtterancesRef.current = editedUtterances
	editedSpeakersRef.current = editedSpeakers

	// Reset state when artifact changes
	useEffect(() => {
		if (artifactId !== lastArtifactIdRef.current) {
			setEditedUtterances(new Map())
			setEditedSpeakers(new Map())
			hasInitializedRef.current = false
			setSaveStatus('saved')
			lastSavedEditsRef.current = { texts: new Map(), speakers: new Map() }
			hasUnsavedChangesRef.current = false
			setHasUnsavedChanges(false)
			isSavingRef.current = false
			lastArtifactIdRef.current = artifactId
		}
	}, [artifactId])

	// Initialize when utterances are available
	useEffect(() => {
		if (!utterances || utterances.length === 0) {
			setEditedUtterances(new Map())
			setEditedSpeakers(new Map())
			hasInitializedRef.current = false
			setSaveStatus('idle')
			lastSavedEditsRef.current = { texts: new Map(), speakers: new Map() }
			hasUnsavedChangesRef.current = false
			setHasUnsavedChanges(false)
			return
		}

		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true
			setSaveStatus('saved')
		}
	}, [utterances])

	const performSave = useCallback(
		async (edits: EditsState) => {
			if (!utterances || utterances.length === 0) return
			if (!hasPendingEdits(edits)) return

			if (artifactId !== lastArtifactIdRef.current) return
			if (isSavingRef.current) return

			isSavingRef.current = true
			setSaveStatus('saving')

			try {
				const plainText = reconstructPlainText(utterances, edits.texts, edits.speakers)
				const success = await onSave(plainText)

				if (success) {
					setSaveStatus('saved')
					hasUnsavedChangesRef.current = false
					setHasUnsavedChanges(false)
					lastSavedEditsRef.current = {
						texts: new Map(edits.texts),
						speakers: new Map(edits.speakers),
					}

					setTimeout(() => {
						isSavingRef.current = false
					}, SAVE_LOCK_DELAY_MS)
				} else {
					setSaveStatus('error')
					isSavingRef.current = false
				}
			} catch (err) {
				console.error('Error saving transcript:', err)
				setSaveStatus('error')
				isSavingRef.current = false
			}
		},
		[artifactId, utterances, onSave]
	)

	const save = useCallback(async () => {
		if (!hasInitializedRef.current || !hasUnsavedChangesRef.current) return

		const edits: EditsState = {
			texts: new Map(editedUtterancesRef.current),
			speakers: new Map(editedSpeakersRef.current),
		}

		await performSave(edits)
	}, [performSave])

	const updateUtterance = useCallback(
		(index: number, text: string, newSpeaker?: string) => {
			if (!utterances || index >= utterances.length) return

			setEditedUtterances((prev) => {
				const next = new Map(prev)
				const originalText = utterances[index].text

				if (text === originalText) {
					next.delete(index)
				} else {
					next.set(index, text)
				}
				return next
			})

			if (newSpeaker !== undefined) {
				setEditedSpeakers((prev) => {
					const next = new Map(prev)
					const originalSpeaker = utterances[index].speaker

					if (newSpeaker === originalSpeaker) {
						next.delete(index)
					} else {
						next.set(index, newSpeaker)
					}
					return next
				})
			}

			hasUnsavedChangesRef.current = true
			setHasUnsavedChanges(true)

			if (saveStatus === 'error' || saveStatus === 'saved') {
				setSaveStatus('idle')
			}
		},
		[utterances, saveStatus]
	)

	// Reconcile dirty state when edits revert to last saved snapshot
	useEffect(() => {
		const edits: EditsState = { texts: editedUtterances, speakers: editedSpeakers }
		const dirty = !editsStateEqual(edits, lastSavedEditsRef.current) && hasPendingEdits(edits)
		hasUnsavedChangesRef.current = dirty
		setHasUnsavedChanges(dirty)
		if (!dirty && saveStatus !== 'saving' && saveStatus !== 'error') {
			setSaveStatus('saved')
		}
	}, [editedUtterances, editedSpeakers, saveStatus])

	return {
		editedUtterances,
		editedSpeakers,
		saveStatus,
		updateUtterance,
		hasUnsavedChanges,
		save,
	}
}
